// src/pages/EditTechnicalSupport.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface TechnicalSupportData {
  id?: number;
  questions_asked: string;
  resolved: boolean;
  meeting_scheduled: boolean;
  meeting_date: string | null;
}

interface Report {
  id: number;
  created_at: string;
}

const EditTechnicalSupport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<TechnicalSupportData>({
    id: undefined,
    questions_asked: "",
    resolved: false,
    meeting_scheduled: false,
    meeting_date: null,
  });
  const [error, setError] = useState("");
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // 1) Fetch or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((fetchedReports: Report[]) => {
        if (Array.isArray(fetchedReports) && fetchedReports.length > 0) {
          setNewestReportId(fetchedReports[0].id);
        } else {
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          })
            .then((res) => res.json())
            .then((newReport) => setNewestReportId(newReport.id))
            .catch(() => setError("Unable to create a new report."));
        }
      })
      .catch(() => setError("Unable to fetch reports."));
  }, [id]);

  // 2) Load technical support data
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/technical-support`)
      .then((res) => res.json())
      .then((fetchedData: any[]) => {
        if (!Array.isArray(fetchedData) || fetchedData.length === 0) {
          // Create default record
          const defaultData = {
            dealer_id: parseInt(id!, 10),
            questions_asked: "No questions provided",
            resolved: false,
            meeting_scheduled: false,
            meeting_date: null,
          };
          fetch(`/api/reports/${newestReportId}/technical-support`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(defaultData),
          })
            .then((res) => res.json())
            .then((created) => {
              setData({
                id: created.id,
                questions_asked: created.questions_asked || "",
                resolved: created.resolved || false,
                meeting_scheduled: created.meeting_scheduled || false,
                meeting_date: created.meeting_date || null,
              });
            })
            .catch(() => setError("Error creating technical support record."));
        } else {
          const tech = fetchedData[0];
          setData({
            id: tech.id,
            questions_asked: tech.questions_asked || "",
            resolved: tech.resolved || false,
            meeting_scheduled: tech.meeting_scheduled || false,
            meeting_date: tech.meeting_date || null,
          });
        }
      })
      .catch(() => setError("Error fetching technical support data."));
  }, [newestReportId, id]);

  // Handle input/textarea changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  // Handle dropdown changes for booleans
  const handleSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const boolVal = value === "Yes";
    if (name === "meeting_scheduled") {
      // If turning off scheduling, clear the date
      setData({
        ...data,
        meeting_scheduled: boolVal,
        meeting_date: boolVal ? data.meeting_date : null,
      });
    } else {
      setData({ ...data, [name]: boolVal });
    }
  };

  // Submit updated data
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!data.id || !newestReportId) {
      setError("Technical support record not found.");
      return;
    }
    // Ensure meeting_date is null if not scheduled
    const payload = {
      ...data,
      meeting_date: data.meeting_scheduled ? data.meeting_date : null,
    };
    fetch(`/api/reports/${newestReportId}/technical-support`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((b) => {
            throw new Error(b.error || "Error updating technical support data");
          });
        }
      })
      .then(() => navigate(`/dealer/${id}`))
      .catch((err) => setError(err.message));
  };

  // Styles
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" };
  const headerStyle: React.CSSProperties = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Technical Support</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Questions Asked:</label>
          <textarea
            name="questions_asked"
            style={{ ...inputStyle, height: "100px" }}
            value={data.questions_asked}
            onChange={handleChange}
            placeholder="Enter questions asked"
          />

          <label style={labelStyle}>Resolved:</label>
          <select
            name="resolved"
            value={data.resolved ? "Yes" : "No"}
            onChange={handleSelectChange}
            style={inputStyle}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          <label style={labelStyle}>Meeting Scheduled:</label>
          <select
            name="meeting_scheduled"
            value={data.meeting_scheduled ? "Yes" : "No"}
            onChange={handleSelectChange}
            style={inputStyle}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          {data.meeting_scheduled && (
            <>
              <label style={labelStyle}>Date of Scheduled Meeting:</label>
              <input
                type="date"
                name="meeting_date"
                style={inputStyle}
                value={
                  data.meeting_date
                    ? new Date(data.meeting_date).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </>
          )}

          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTechnicalSupport;
